/**
 * World Magnetic Model (WMM2025) — a small, dependency-free evaluator so the
 * plotter can work out the local magnetic variation (declination) from where the
 * boat is and what the date is, the same way a GPS or a fitted plotter does. That
 * lets the True/Magnetic display run an Auto mode: nothing to type in, the
 * variation just follows the model as we move.
 *
 * Why a model at all? Variation isn't a fixed number. It changes from place to
 * place, and it drifts year on year as the magnetic pole wanders. So the model
 * carries a base field for its epoch plus a yearly rate of change, and we age the
 * base field forward to today's date before reading it. WMM2025 is the current
 * set, valid from 2025.0 to 2030.0.
 *
 * The coefficients below are the official NOAA/BGS WMM2025 values (the public
 * .COF set), embedded so there's nothing to fetch at sea. The maths is the
 * standard spherical-harmonic synthesis from the WMM technical report: Schmidt
 * semi-normalised Legendre functions to degree and order 12, evaluated at the
 * boat's geocentric position, then rotated back into the geodetic frame we
 * navigate in. The output is declination in degrees, East-positive, which is
 * exactly the sign the rest of the app uses for variation (True = Magnetic +
 * Variation). Validated to 4 decimal places against a reference WMM2025
 * implementation across a spread of test points (see wmm.test.ts).
 */

/** Highest degree/order in the model. */
const N_MAX = 12;
/** Decimal year the base coefficients are given for. */
const EPOCH = 2025.0;
/** The model's validity window (decimal years). Outside it we clamp rather than
 * extrapolate into nonsense, so a wrong clock can't throw a wild variation. */
export const WMM_VALID_FROM = 2025.0;
export const WMM_VALID_TO = 2030.0;

// WGS84 ellipsoid (km) and the WMM geomagnetic reference radius (km).
const A = 6378.137;
const B = 6356.7523142;
const RE = 6371.2;
const EPSSQ = 1 - (B * B) / (A * A);

// Coefficients, indexed by i = n*(n+1)/2 + m (a [0] placeholder sits at index 0).
// G/H are the base main-field terms (nT); DG/DH are the secular-variation rates
// (nT per year) we age the base field by.
const G: readonly number[] = [
  0, -29351.8, -1410.8, -2556.6, 2951.1, 1649.3, 1361, -2404.1, 1243.8, 453.6,
  895, 799.5, 55.7, -281.1, 12.1, -233.2, 368.9, 187.2, -138.7, -142,
  20.9, 64.4, 63.8, 76.9, -115.7, -40.9, 14.9, -60.7, 79.5, -77,
  -8.8, 59.3, 15.8, 2.5, -11.1, 14.2, 23.2, 10.8, -17.5, 2,
  -21.7, 16.9, 15, -16.8, 0.9, 4.6, 7.8, 3, -0.2, -2.5,
  -13.1, 2.4, 8.6, -8.7, -12.9, -1.3, -6.4, 0.2, 2, -1,
  -0.6, -0.9, 1.5, 0.9, -2.7, -3.9, 2.9, -1.5, -2.5, 2.4,
  -0.6, -0.1, -0.6, -0.1, 1.1, -1, -0.2, 2.6, -2, -0.2,
  0.3, 1.2, -1.3, 0.6, 0.6, 0.5, -0.1, -0.4, -0.2, -1.3,
  -0.7,
];
const H: readonly number[] = [
  0, 0, 4545.4, 0, -3133.6, -815.1, 0, -56.6, 237.5, -549.5,
  0, 278.6, -133.9, 212, -375.6, 0, 45.4, 220.2, -122.9, 43,
  106.1, 0, -18.4, 16.8, 48.8, -59.8, 10.9, 72.7, 0, -48.9,
  -14.4, -1, 23.4, -7.4, -25.1, -2.3, 0, 7.1, -12.6, 11.4,
  -9.7, 12.7, 0.7, -5.2, 3.9, 0, -24.8, 12.2, 8.3, -3.3,
  -5.2, 7.2, -0.6, 0.8, 10, 0, 3.3, 0, 2.4, 5.3,
  -9.1, 0.4, -4.2, -3.8, 0.9, -9.1, 0, 0, 2.9, -0.6,
  0.2, 0.5, -0.3, -1.2, -1.7, -2.9, -1.8, -2.3, 0, -1.3,
  0.7, 1, -1.4, 0, 0.6, -0.1, 0.8, 0.1, -1, 0.1,
  0.2,
];
const DG: readonly number[] = [
  0, 12, 9.7, -11.6, -5.2, -8, -1.3, -4.2, 0.4, -15.6,
  -1.6, -2.4, -6, 5.6, -7, 0.6, 1.4, 0, 0.6, 2.2,
  0.9, -0.2, -0.4, 0.9, 1.2, -0.9, 0.3, 0.9, 0, -0.1,
  -0.1, 0.5, -0.1, -0.8, -0.8, 0.8, -0.1, 0.2, 0, 0.5,
  -0.1, 0.3, 0.2, 0, 0.2, 0, -0.1, 0.1, 0.3, -0.3,
  0, 0.3, -0.1, 0.1, -0.1, 0.1, 0, 0.1, 0.1, 0,
  -0.3, 0, -0.1, -0.1, 0, 0, 0, 0, 0, 0,
  0, -0.1, 0, 0, -0.1, -0.1, -0.1, -0.1, 0, 0,
  0, 0, 0, 0, 0.1, 0, 0, 0, -0.1, 0,
  -0.1,
];
const DH: readonly number[] = [
  0, 0, -21.5, 0, -27.7, -12.1, 0, 4, -0.3, -4.1,
  0, -1.1, 4.1, 1.6, -4.4, 0, -0.5, 2.2, 0.4, 1.7,
  1.9, 0, 0.3, -1.6, -0.4, 0.9, 0.7, 0.9, 0, 0.6,
  0.5, -0.8, 0, -1, 0.6, -0.2, 0, -0.2, 0.5, -0.4,
  0.4, -0.5, -0.6, 0.3, 0.2, 0, -0.3, 0.3, -0.3, 0.3,
  0.2, -0.1, -0.2, 0.4, 0.1, 0, 0, 0, -0.2, 0.1,
  -0.1, 0.1, 0, -0.1, 0.2, 0, 0, 0, 0.1, 0,
  0.1, 0, 0, 0.1, 0, 0, 0, 0, 0, 0,
  0, -0.1, 0.1, 0, 0, 0, 0, 0, 0, 0,
  -0.1,
];

/** Decimal year for a date, matching the WMM reference (365-day year fraction). */
export function decimalYear(date: Date): number {
  const y = date.getUTCFullYear();
  const frac = (date.valueOf() - Date.UTC(y, 0, 1)) / (1000 * 3600 * 24 * 365);
  return y + frac;
}

/** Age the base field to a given offset (years) from the model epoch. */
function agedCoeffs(dyear: number): { g: number[]; h: number[] } {
  const g: number[] = [0];
  const h: number[] = [0];
  for (let n = 1; n <= N_MAX; n++) {
    for (let m = 0; m <= n; m++) {
      const i = (n * (n + 1)) / 2 + m;
      g[i] = G[i] + dyear * DG[i];
      h[i] = H[i] + dyear * DH[i];
    }
  }
  return { g, h };
}

/** Schmidt semi-normalised associated Legendre functions and their derivatives. */
function legendre(sinPhi: number): { pcup: number[]; dpcup: number[] } {
  const x = sinPhi;
  const z = Math.sqrt((1 - x) * (1 + x));
  const pcup: number[] = [1.0];
  const dpcup: number[] = [0.0];
  const sq: number[] = [1.0];
  for (let n = 1; n <= N_MAX; n++) {
    for (let m = 0; m <= n; m++) {
      const i = (n * (n + 1)) / 2 + m;
      if (n === m) {
        const i1 = ((n - 1) * n) / 2 + m - 1;
        pcup[i] = z * pcup[i1];
        dpcup[i] = z * dpcup[i1] + x * pcup[i1];
      } else if (n === 1 && m === 0) {
        const i1 = ((n - 1) * n) / 2 + m;
        pcup[i] = x * pcup[i1];
        dpcup[i] = x * dpcup[i1] - z * pcup[i1];
      } else if (n > 1 && n !== m) {
        const i1 = ((n - 2) * (n - 1)) / 2 + m;
        const i2 = ((n - 1) * n) / 2 + m;
        if (m > n - 2) {
          pcup[i] = x * pcup[i2];
          dpcup[i] = x * dpcup[i2] - z * pcup[i2];
        } else {
          const k = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3));
          pcup[i] = x * pcup[i2] - k * pcup[i1];
          dpcup[i] = x * dpcup[i2] - z * pcup[i2] - k * dpcup[i1];
        }
      }
    }
  }
  for (let n = 1; n <= N_MAX; n++) {
    let i = (n * (n + 1)) / 2;
    const i1 = ((n - 1) * n) / 2;
    sq[i] = (sq[i1] * (2 * n - 1)) / n;
    for (let m = 1; m <= n; m++) {
      i = (n * (n + 1)) / 2 + m;
      const j = (n * (n + 1)) / 2 + m - 1;
      sq[i] = sq[j] * Math.sqrt(((n - m + 1) * (m === 1 ? 2 : 1)) / (n + m));
    }
  }
  for (let n = 1; n <= N_MAX; n++) {
    for (let m = 0; m <= n; m++) {
      const i = (n * (n + 1)) / 2 + m;
      pcup[i] *= sq[i];
      dpcup[i] *= -sq[i];
    }
  }
  return { pcup, dpcup };
}

/**
 * Magnetic declination (variation) in degrees at a position and date, computed
 * from WMM2025. East-positive, matching the app's variation convention. Height is
 * taken as sea level. Returns 0 for a non-finite input rather than NaN, so a bad
 * fix can't poison the display.
 */
export function magneticDeclinationDeg(latDeg: number, lonDeg: number, date: Date = new Date()): number {
  if (!Number.isFinite(latDeg) || !Number.isFinite(lonDeg)) return 0;
  const yr = Math.min(WMM_VALID_TO, Math.max(WMM_VALID_FROM, decimalYear(date)));
  const dyear = yr - EPOCH;
  const { g, h } = agedCoeffs(dyear);

  // Geodetic latitude -> geocentric (spherical) latitude and radius (sea level).
  const coslat = Math.cos((latDeg * Math.PI) / 180);
  const sinlat = Math.sin((latDeg * Math.PI) / 180);
  const rc = A / Math.sqrt(1 - EPSSQ * sinlat * sinlat);
  const xp = rc * coslat;
  const zp = rc * (1 - EPSSQ) * sinlat;
  const r = Math.sqrt(xp * xp + zp * zp);
  const phig = (180 / Math.PI) * Math.asin(zp / r);

  const { pcup, dpcup } = legendre(Math.sin((Math.PI / 180) * phig));

  const cosl = Math.cos((Math.PI / 180) * lonDeg);
  const sinl = Math.sin((Math.PI / 180) * lonDeg);
  const cosm: number[] = [1.0, cosl];
  const sinm: number[] = [0.0, sinl];
  const rrp: number[] = [(RE / r) * (RE / r)];
  for (let n = 1; n <= N_MAX; n++) rrp[n] = rrp[n - 1] * (RE / r);
  for (let m = 2; m <= N_MAX; m++) {
    cosm[m] = cosm[m - 1] * cosl - sinm[m - 1] * sinl;
    sinm[m] = cosm[m - 1] * sinl + sinm[m - 1] * cosl;
  }

  let bx = 0;
  let by = 0;
  let bz = 0;
  for (let n = 1; n <= N_MAX; n++) {
    for (let m = 0; m <= n; m++) {
      const i = (n * (n + 1)) / 2 + m;
      bz -= rrp[n] * (g[i] * cosm[m] + h[i] * sinm[m]) * (n + 1) * pcup[i];
      by += rrp[n] * (g[i] * sinm[m] - h[i] * cosm[m]) * m * pcup[i];
      bx -= rrp[n] * (g[i] * cosm[m] + h[i] * sinm[m]) * dpcup[i];
    }
  }
  const cosphi = Math.cos((Math.PI / 180) * phig);
  if (Math.abs(cosphi) > 1e-10) {
    by = by / cosphi;
  } else {
    // Near the geographic poles the usual By division is singular; the WMM report
    // gives this special sum instead. We navigate nowhere near here, but it keeps
    // the evaluator correct everywhere rather than blowing up at 90 deg.
    by = 0;
    let sq1 = 1.0;
    const ps: number[] = [1];
    const sp = Math.sin((Math.PI / 180) * phig);
    for (let n = 1; n <= N_MAX; n++) {
      const i = (n * (n + 1)) / 2 + 1;
      const sq2 = (sq1 * (2 * n - 1)) / n;
      const sq3 = sq2 * Math.sqrt((2 * n) / (n + 1));
      sq1 = sq2;
      if (n === 1) ps[n] = ps[n - 1];
      else {
        const k = ((n - 1) * (n - 1) - 1) / ((2 * n - 1) * (2 * n - 3));
        ps[n] = sp * ps[n - 1] - k * ps[n - 2];
      }
      by += rrp[n] * (g[i] * sinm[1] - h[i] * cosm[1]) * ps[n] * sq3;
    }
  }

  // Rotate the field from the geocentric frame back to geodetic, then declination
  // is the horizontal field's angle east of true north.
  const psi = (Math.PI / 180) * (phig - latDeg);
  const bxg = bx * Math.cos(psi) - bz * Math.sin(psi);
  return (180 / Math.PI) * Math.atan2(by, bxg);
}
